/**
 * Importação do protocolo de assinatura (E77.F1, ADR-0078).
 *
 * Módulo próprio, e não mais uma seção de `exame_eletronico.ts`: aquele arquivo
 * já estava no teto de 400 linhas do lint, e a importação é uma jornada
 * distinta da verificação — sobe um documento, propõe campos, e não grava nada.
 *
 * A garantia que atravessa o módulo: o que volta daqui é **sugestão**. Quem
 * grava é o perito, pelo `salvarProtocoloDoCaso` (§2.2).
 */
import type { AxiosInstance } from 'axios';

import {
  classifyExameEletronicoError,
  type ProtocoloAssinaturaCasoData,
} from '@/lib/exame_eletronico';

function basePath(caseId: string): string {
  return `/casos/${caseId}/exame-eletronico`;
}

/** De onde veio um campo sugerido: lido por rótulo, ou pela leitura assistida. */
export type ProvenienciaCampo = 'regex' | 'assistida';

export interface AnexoDoProtocoloData {
  nome_arquivo: string;
  content_sha256: string;
  importado_em: string;
}

export interface ProtocoloSugeridoData extends ProtocoloAssinaturaCasoData {
  proveniencia: Record<string, ProvenienciaCampo>;
}

export interface ProtocoloImportadoData {
  sugestao: ProtocoloSugeridoData;
  anexo: AnexoDoProtocoloData;
  /** SHA-256 da PEÇA — gravado pela rota do código, não pelo PUT do protocolo. */
  codigo_declarado_sugerido: string | null;
  /** Sobrou campo E vale oferecer a leitura assistida (que é paga). */
  residuo_disponivel: boolean;
  /** Por que a leitura assistida não completou, quando foi tentada. */
  leitura_falhou: string | null;
}

/**
 * Sobe o PDF do protocolo e recebe a sugestão do passo determinístico.
 *
 * **Não grava nada**: o retorno é proposta, e o perito confirma pelo
 * `salvarProtocoloDoCaso` (ADR-0078 §2.2).
 */
export async function importarProtocolo(
  client: AxiosInstance,
  caseId: string,
  arquivo: File,
): Promise<ProtocoloImportadoData> {
  const form = new FormData();
  form.append('file', arquivo);
  try {
    const { data } = await client.post<ProtocoloImportadoData>(
      `${basePath(caseId)}/protocolo/importar`,
      form,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Leitura assistida do que a extração determinística não encontrou.
 *
 * Ação paga e opt-in (§2.4). Falha do provedor NÃO levanta: volta em
 * `leitura_falhou`, e o formulário manual segue aberto (§2.6).
 */
export async function lerResiduoDoProtocolo(
  client: AxiosInstance,
  caseId: string,
): Promise<ProtocoloImportadoData> {
  try {
    const { data } = await client.post<ProtocoloImportadoData>(
      `${basePath(caseId)}/protocolo/importar/residuo`,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}
